import { ChronicDiseaseApproveDraftCommand } from '@dreryk/egabinet-patient-medical-information-frontend-api';
import { Icd10Code } from '@dreryk/egabinet-icd-dictionary-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import { patientMedicalInformationTranslationKeys } from '../../../translations';
import { refetchAllMedicalInformation } from '../../shared/refetchAllMedicalInformation';

import { ChronicDiseaseEntryFormBean } from './chronicDiseaseEntryForm.types';
import { forSpecialist } from './ChronicDiseaseEntryForm.utils';

type ChronicDiseaseApproveDraftActionRenderer = (renderProps: {
   createChronicDisease: (formData: ChronicDiseaseEntryFormBean) => Promise<void>;
}) => JSX.Element;

type ChronicDiseaseApproveDraftActionProps = {
   children: ChronicDiseaseApproveDraftActionRenderer;
   onSuccess: VoidFunction;
   medicalInformationId: number;
};

export const ChronicDiseaseApproveDraftAction = (props: ChronicDiseaseApproveDraftActionProps): JSX.Element => {
   const updatingDoctorPersonalDataAvailable = protectFeature('updatingDoctorPersonalData');

   const { executeAsync: createChronicDisease } = useCqrsCommand<void, ChronicDiseaseEntryFormBean>(
      (parameters) => {
         const command = new ChronicDiseaseApproveDraftCommand();
         command.id = props.medicalInformationId;
         command.confidentialityLevel = parameters.confidentialityLevel;
         command.description = Choice.withUseDefaultIfNull(parameters.description);
         command.diagnosedByParameters = {
            currentPractitioner: parameters.diagnosedByCurrentPractitioner,
            name: Slot.emptyIfNull(parameters.diagnosedByName),
            licenceNumber: Slot.emptyIfNull(parameters.diagnosedByLicenceNumber),
         };
         command.diagnosisCreationDate = parameters.diagnosisCreationDate;
         command.diagnosisIcd10Code = Icd10Code.of(parameters.disease!.icd10Code);

         if (parameters.diagnosisRemovalDate) {
            command.removalParameters = Slot.of({
               removalReason: parameters.removalReason!,
               diagnosisRemovalDate: parameters.diagnosisRemovalDate,
               ...(forSpecialist(updatingDoctorPersonalDataAvailable) && {
                  diagnosedByParameters: Choice.of({
                     name: parameters.removalDiagnosisByName!,
                     licenceNumber: parameters.removalDiagnosisByLicenceNumber!,
                  }),
               }),
            });
         }

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(patientMedicalInformationTranslationKeys.successfullyAddedNewEntry);
            refetchAllMedicalInformation();
            props.onSuccess();
         },
      }
   );

   return props.children({
      createChronicDisease: createChronicDisease,
   });
};
